/*
 * Helper for rendering the time slot label of a member's booking on a
 * bookable event.
 */

function isValidTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function formatBookingSlotLabel(
  value,
  timeZone,
  { withDate = false } = {},
) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const opts = { hour: "numeric", minute: "2-digit", hour12: true };
  if (withDate) {
    opts.weekday = "short";
    opts.month = "short";
    opts.day = "numeric";
  }
  if (isValidTimeZone(timeZone)) opts.timeZone = timeZone;
  return d.toLocaleString("en-US", opts);
}

function asValidDate(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value == null || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getPtSessionInstanceId(value) {
  if (!value || typeof value !== "object") return null;
  const raw =
    value.ptSessionInstanceId ??
    value.pt_session_instance_id ??
    value.sessionInstanceId ??
    value.instanceId;
  if (raw && typeof raw === "object") {
    return String(raw._id ?? raw.id ?? "") || null;
  }
  return raw == null || raw === "" ? null : String(raw);
}

export function getPtBookingRange(booking) {
  if (!booking || typeof booking !== "object") return null;
  const start = asValidDate(
    booking.start ??
      booking.start_datetime ??
      booking.start_time ??
      booking.start_date,
  );
  if (!start) return null;
  let end = asValidDate(
    booking.end ?? booking.end_datetime ?? booking.end_time,
  );
  if (!end) {
    const duration = Number(booking.duration_minutes ?? booking.duration);
    if (Number.isFinite(duration) && duration > 0) {
      end = new Date(start.getTime() + duration * 60000);
    }
  }
  if (!end) end = new Date(start.getTime() + 30 * 60000);
  return { start, end };
}

export function ptSessionSlotKey(sessionId, start, end, instanceId = null) {
  const id = String(sessionId ?? "");
  const instance = instanceId == null ? "" : String(instanceId);
  const startDate = asValidDate(start);
  const endDate = asValidDate(end);
  if (instance) return `${id}::instance:${instance}`;
  if (!startDate) return null;
  return `${id}::range:${startDate.toISOString()}::${endDate ? endDate.toISOString() : ""}`;
}

export function ptBookingMatchesOccurrence(booking, occurrence, sessionId) {
  const bookingInstanceId = getPtSessionInstanceId(booking);
  const occurrenceInstanceId = getPtSessionInstanceId(occurrence);
  if (bookingInstanceId && occurrenceInstanceId) {
    return Boolean(
      bookingInstanceId &&
      occurrenceInstanceId &&
      bookingInstanceId === occurrenceInstanceId,
    );
  }
  const bookingRange = getPtBookingRange(booking);
  if (!bookingRange || !(occurrence?.start instanceof Date)) return false;
  const occurrenceEnd =
    occurrence.end instanceof Date && !Number.isNaN(occurrence.end.getTime())
      ? occurrence.end
      : null;
  if (!occurrenceEnd) return false;
  if (
    ptSessionSlotKey(sessionId, bookingRange.start, bookingRange.end) ===
    ptSessionSlotKey(sessionId, occurrence.start, occurrenceEnd)
  ) {
    return true;
  }
  return (
    bookingRange.start.getTime() >= occurrence.start.getTime() &&
    bookingRange.end.getTime() <= occurrenceEnd.getTime()
  );
}

export function pickClassCapacity(c) {
  if (!c || typeof c !== "object") return null;
  const keys = [
    "capacity",
    "Capacity",
    "max_capacity",
    "maxCapacity",
    "class_capacity",
    "classCapacity",
    "max_spots",
    "maxSpots",
    "spots",
  ];
  for (const k of keys) {
    const v = c[k];
    if (v != null && v !== "" && !Number.isNaN(Number(v))) {
      const n = Number(v);
      if (n >= 0) return n;
    }
  }
  return null;
}

export function pickClassEnrolledCount(c) {
  if (!c || typeof c !== "object") return null;
  const keys = [
    "enrolled_count",
    "enrolledCount",
    "bookings_count",
    "bookingsCount",
    "current_bookings",
    "currentBookings",
    "spots_taken",
    "spotsTaken",
    "headcount",
    "registered_count",
    "registeredCount",
    "filled",
    "attendee_count",
    "attendeeCount",
  ];
  for (const k of keys) {
    const v = c[k];
    if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

export function getPtCapacitySummary(value, bookedOverride) {
  const normalizedCapacity = pickClassCapacity(value);
  const rawBooked = bookedOverride ?? pickClassEnrolledCount(value) ?? 0;
  const bookedNumber = Number(rawBooked);
  const booked = Number.isFinite(bookedNumber) ? Math.max(0, bookedNumber) : 0;
  const remaining =
    normalizedCapacity == null
      ? null
      : Math.max(0, normalizedCapacity - booked);
  return {
    capacity: normalizedCapacity,
    booked,
    remaining,
    isFull: normalizedCapacity != null && booked >= normalizedCapacity,
  };
}
